import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CreditCard } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from '../components/AuthModal';

const DELIVERY_FEE = 1;

export const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useCart();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  React.useEffect(() => {
    if (state.items.length === 0) {
      navigate('/');
    }
  }, [state.items.length, navigate]);

  const handleNext = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    navigate('/checkout/payment');
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    navigate('/checkout/payment');
  };

  const subtotal = state.total;
  const total = subtotal + DELIVERY_FEE;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Order Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Order Summary</h2>
            </div>
            <div className="space-y-4">
              {state.items.map((item) => (
                <div 
                  key={`${item.productId}-${item.size}-${item.type}`}
                  className="flex justify-between items-start border-b border-gray-200 dark:border-gray-700 pb-4"
                >
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{item.brand}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {item.size} - {item.type === 'new' ? 'New Purchase' : 'Refill'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    KSH {item.price * item.quantity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 sticky top-24">
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payment Summary</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Subtotal</span>
                <span>KSH {subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Delivery Fee</span>
                <span>KSH {DELIVERY_FEE}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-white">
                  <span>Total</span>
                  <span>KSH {total}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleNext}
              className="w-full mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Proceed to Payment
            </button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
};